'use client';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';
import { useFormik } from 'formik';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import Button from '@/components/Button';
import FormCheckbox from '@/components/FormCheckbox';
import FormInput from '@/components/FormInput';
import SocialLogin from '@/components/SocialLogin';
import { useAuthenticate } from '@/services/hooks/auth/user.query';
import { loginFormValidationSchema } from '@/utils/validation';

import styles from './styles.module.scss';
import { CloseProps, Props } from './types';

const LoginForm = ({
  openToast,
  onLoggedIn,
  close,
  redirectUrl,
  variant,
}: Props & CloseProps) => {
  const [error, setError] = useState('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { mutateAsync: signIn, isLoading } = useAuthenticate();
  const [passwordShown, setPasswordShown] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginFormValidationSchema,
    onSubmit: async values => {
      try {
        if (redirectUrl) {
          await signIn({ ...values, rememberMe: true });
        } else {
          await signIn({ ...values, rememberMe });
        }

        if (redirectUrl) {
          router.replace(redirectUrl);
        } else {
          router.replace('/');
        }
        openToast?.();
        if (onLoggedIn) {
          onLoggedIn(true);
        }
      } catch {
        setError(
          'Nie udało się zalogować, sprawdź poprawność wprowadzonych danych.',
        );
        if (onLoggedIn) {
          onLoggedIn(false);
        }
      }
    },
  });

  const togglePassword = () => {
    setPasswordShown(prev => !prev);
  };

  return (
    <div className={styles.container}>
      {searchParams?.get('register') && (
        <p className={styles.notification}>
          Rejestracja przebiegła pomyślnie, możesz się zalogować na swoje konto
        </p>
      )}
      <form
        id="loginForm"
        onSubmit={formik.handleSubmit}
        className={styles.formWrapper}
      >
        <div
          className={cn(styles.sectionWrapper, {
            [styles.noMargin]: error,
          })}
        >
          <FormInput
            label="Adres e-mail"
            type="text"
            id="email"
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('email')}
            value={formik.values.email}
            isRequired
            isError={Boolean(formik.errors.email && formik.touched.email)}
            errorMsg={formik.errors.email}
          />
          <FormInput
            label="Hasło"
            type={passwordShown ? 'text' : 'password'}
            id="password"
            name="password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('password')}
            value={formik.values.password}
            isRequired
            isError={Boolean(formik.errors.password && formik.touched.password)}
            errorMsg={formik.errors.password}
          >
            <Button onClick={togglePassword} isIconType>
              {!passwordShown ? (
                <EyeIcon className={styles.openEyeIcon} />
              ) : (
                <EyeSlashIcon className={styles.closedEyeIcon} />
              )}
            </Button>
          </FormInput>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.forgotPasswordWrapper}>
          <Link
            href="/reset-password"
            className={styles.forgotPasswordLink}
            onClick={close}
          >
            Zapomniałeś hasła?
          </Link>
        </div>
        <div className={styles.rememberMe}>
          <FormCheckbox
            onChange={e => setRememberMe(e.target.checked)}
            checked={rememberMe}
            label={
              <label className={styles.checkboxLabel}>Zapamiętaj mnie</label>
            }
          />
        </div>

        <Button
          disabled={isLoading}
          type="submit"
          isCapitalize
          isBiggerPadding
          variant={variant === 'checkout' ? 'checkout' : 'secondary'}
          id="loginButton"
          className={styles.loginButton}
          onClick={formik.handleSubmit}
        >
          Zaloguj się
        </Button>
      </form>
      <p className={styles.border}>
        <span className={styles.borderText}>lub kontynuuj z</span>
      </p>
      <SocialLogin redirectUrl={redirectUrl} />
    </div>
  );
};

export default LoginForm;
